/* eslint-disable @typescript-eslint/naming-convention -- API error payload uses PascalCase in JSON */
import { useEffect, useRef, useState } from "react";

import Sentry from "@similarweb/sentry-logger";
import { ResponseError } from "app/services/fetchService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const NUMBER_OF_SEGMENTS_DONT_FIT_REQUESTED_PERIOD = "NumberOfSegmentsDontFitRequestedPeriod" as const;

const MAX_DURATION_RECOVERY_ATTEMPTS = 3;

export type SegmentsRequestValidationResult = {
    ErrorMessage?: string;
    ErrorCode: typeof NUMBER_OF_SEGMENTS_DONT_FIT_REQUESTED_PERIOD;
    MaxAllowedMonths?: number;
};

/**
 * Parses `error` when it is a 409 {@link ResponseError} whose `name` JSON matches the BE
 * segments-request validation shape with {@link SegmentsRequestValidationResult} fields present.
 * Otherwise returns null.
 */
function parseSegmentsRequestValidationResult(
    error: unknown,
): Required<SegmentsRequestValidationResult> | null {
    if (!(error instanceof ResponseError) || error.status !== 409) {
        return null;
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(error.name);
    } catch {
        return null;
    }

    if (typeof parsed !== "object" || parsed === null) {
        return null;
    }

    if (
        !("ErrorCode" in parsed) ||
        typeof parsed.ErrorCode !== "string" ||
        parsed.ErrorCode !== NUMBER_OF_SEGMENTS_DONT_FIT_REQUESTED_PERIOD
    ) {
        return null;
    }

    if (!("ErrorMessage" in parsed) || typeof parsed.ErrorMessage !== "string") {
        return null;
    }

    if (!("MaxAllowedMonths" in parsed) || typeof parsed.MaxAllowedMonths !== "number") {
        return null;
    }

    return {
        ErrorCode: parsed.ErrorCode,
        ErrorMessage: parsed.ErrorMessage,
        MaxAllowedMonths: parsed.MaxAllowedMonths,
    };
}

export const useDurationValidationRecovery = (
    error: unknown,
    isSuccess: boolean,
): { isRecovering: boolean } => {
    const [recoveryAttempts, setRecoveryAttempts] = useState(0);
    const processedErrorKeyRef = useRef<string | null>(null);

    const recovery = parseSegmentsRequestValidationResult(error);

    useEffect(() => {
        if (isSuccess) {
            setRecoveryAttempts(0);
            processedErrorKeyRef.current = null;
        }
    }, [isSuccess]);

    useEffect(() => {
        if (!error) {
            processedErrorKeyRef.current = null;
            return;
        }

        const result = parseSegmentsRequestValidationResult(error);
        const key = result ? JSON.stringify([result.ErrorCode, result.ErrorMessage]) : null;

        if (
            !result ||
            recoveryAttempts >= MAX_DURATION_RECOVERY_ATTEMPTS ||
            processedErrorKeyRef.current === key
        ) {
            return;
        }

        processedErrorKeyRef.current = key;
        setRecoveryAttempts((prev) => prev + 1);

        let cancelled = false;

        void (async () => {
            try {
                await proNavigator.updateParams({ duration: `${result.MaxAllowedMonths}m` });
            } catch (e) {
                if (!cancelled) {
                    Sentry.errorTracker({
                        message: "Duration validation recovery: updateParams failed",
                        e: e as Error,
                        level: "warning",
                        params: {
                            extra: {
                                maxAllowedMonths: result.MaxAllowedMonths,
                            },
                        },
                    });
                    processedErrorKeyRef.current = null;
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [error, recoveryAttempts]);

    const isRecovering = recovery != null && recoveryAttempts < MAX_DURATION_RECOVERY_ATTEMPTS;

    return { isRecovering };
};
